import { useEffect, useState } from 'react';
import { CorpoAvisoAtivacaoDia, TITULO_AVISO_DO_DIA, useAtivacaoDia } from '@/components/qualidade/AnaliseDia';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X, CheckCircle, AlertTriangle, AlarmClock, CalendarClock, Info, Bell, Phone, MessageCircle, Clock, Trophy, Star, Flame, ArrowRight, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useLigar } from '@/hooks/useLigar';
import { registrarLigacaoRealizada } from '@/lib/leadHistoricoService';
import { formatarTelefone } from '@/utils/formatters';
import { supabase } from '@/integrations/supabase/client';

interface AvisoPopupProps {
  aviso: any;
  onClose: () => void;
  onConfirmar: (avisoId: string) => Promise<void>;
  onVisualizar: (avisoId: string) => void;
  showHistorico?: boolean;
}

const API_BASE_AVISO = ((import.meta as any).env?.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api';

export function AvisoPopup({ aviso, onClose, onConfirmar, onVisualizar, showHistorico = false }: AvisoPopupProps) {
  const [confirmando, setConfirmando] = useState(false);
  const [adiando, setAdiando] = useState(false);
  const ehAvisoDoDia = (aviso.tipo as string) === 'ativacao_dia' || aviso.metadados?.origem === 'ativacao_dia';
  /* TAREFA VENCENDO (Douglas 02/10/2026): os dois botões mudam a tarefa no back
     (aviso-da-tarefa.ts). Tratar agora abre a conversa em outra aba; Tratar
     depois devolve o aviso em 30 minutos. */
  const ehTarefa = aviso.metadados?.origem === 'tarefa';
  const cardDoDia = useAtivacaoDia(ehAvisoDoDia);
  const { user } = useAuth();
  const navigate = useNavigate();
  const ligar = useLigar();
  const queryClient = useQueryClient();

  const postarNoAviso = async (acao: 'adiar' | 'tratar-agora') => {
    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    const res = await fetch(`${API_BASE_AVISO}/v1/avisos/${aviso.id}/${acao}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j?.error || `Erro ${res.status}`);
    }
    if (ehTarefa) queryClient.invalidateQueries({ queryKey: ['tarefas'] });
  };

  /* "Mensagem de punição não existe" (Douglas 02/10/2026): Tratar depois só
     registra na auditoria e fecha. */
  const handleTratarDepois = async () => {
    setAdiando(true);
    try {
      await postarNoAviso('adiar');
      onClose();
    } catch (err) {
      alert(`Não foi possível adiar: ${(err as Error).message}`);
    } finally {
      setAdiando(false);
    }
  };

  useEffect(() => {
    if (!showHistorico) {
      onVisualizar(aviso.id);
    }
  }, [aviso.id, showHistorico]);

  // (Douglas 16/07) UM beep so no CRM: o do aviso central — som significa
  // "levanta e atende", nao "chegou mais uma mensagem".
  useEffect(() => {
    if (showHistorico) return;
    try {
      const a = new Audio('/notification-whatsapp.mp3');
      a.volume = 0.5;
      a.play().catch(() => {});
    } catch { /* sem audio disponivel */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aviso.id]);

  const handleConfirmar = async () => {
    setConfirmando(true);
    // (Douglas 16/07) Confirmar TAMBEM abre o destino em NOVA ABA quando o aviso
    // aponta um (cta_path_confirm) — ex.: transferencia da IA abre a conversa do
    // inbox sem tirar a pessoa de onde estava. Chave propria pra NAO renderizar
    // o botao generico "Abrir" duplicado (que le cta_path).
    //
    // (Douglas 26/07) ABRE ANTES DO await, E COM PLANO B. O window.open estava
    // DEPOIS do await do RPC: nesse ponto o navegador ja pode ter descartado o
    // "gesto do usuario" do clique, e o bloqueador de pop-up mata a aba em
    // silencio — a pessoa clicava "Vou atender agora", o aviso sumia e a conversa
    // NAO abria. Ela confirmava sem nunca ver o cliente, e isso caia na aba como
    // "VIU · SEM ACAO" (124 casos hoje). Cobranca em cima de botao quebrado.
    // Agora: abre no mesmo tick do clique e, se ainda assim vier bloqueado
    // (janela null), navega na PROPRIA aba — a conversa abre de um jeito ou de
    // outro.
    const ctaPathConfirm = (aviso.metadados as any)?.cta_path_confirm || (aviso.metadados as any)?.cta_path;
    // Pedido novo da Conecta (06/10/2026): "Vi" só marca como visto; a conversa abre
    // pelo botão "Abrir a conversa".
    const ehPedidoConecta = (aviso.metadados as Record<string, unknown> | null)?.origem === 'pedido_conecta';
    const temDestino = !ehPedidoConecta && typeof ctaPathConfirm === 'string' && ctaPathConfirm.startsWith('/');
    const janela = temDestino ? window.open(ctaPathConfirm, '_blank') : null;
    try {
      if (ehTarefa) await postarNoAviso('tratar-agora');
      else await onConfirmar(aviso.id);
      onClose();
      if (temDestino && !janela) navigate(ctaPathConfirm);
    } catch (err) {
      alert('Erro ao confirmar aviso. Tente novamente.');
    } finally {
      setConfirmando(false);
    }
  };

  const getIcone = () => {
    const icones: Record<string, JSX.Element> = {
      informativo: <Info className="w-7 h-7 text-brand-borgonha" />,
      urgente: <AlertTriangle className="w-7 h-7 text-brand-borgonha" />,
      alerta: <AlertTriangle className="w-7 h-7 text-brand-borgonha" />,
      comunicado: <Bell className="w-7 h-7 text-brand-borgonha" />,
      meta: <CheckCircle className="w-7 h-7 text-brand-borgonha" />,
      ranking: <Trophy className="w-7 h-7 text-warning" />,
      motivacional: <Flame className="w-7 h-7 text-warning" />,
      ativacao_dia: <Smartphone className="w-7 h-7 text-brand-borgonha" />,
      celebracao: <Star className="w-7 h-7 text-warning" />,
    };
    if (ehTarefa) return <AlarmClock className="w-7 h-7 text-brand-borgonha" />;
    return icones[aviso.tipo] || icones.informativo;
  };

  const getBorderColor = () => {
    const colors: Record<string, string> = {
      ranking: 'border-warning',
      motivacional: 'border-warning',
      ativacao_dia: 'border-brand-borgonha',
      celebracao: 'border-warning',
    };
    return colors[aviso.tipo] || 'border-brand-borgonha';
  };

  const isCancelamento = aviso.metadados?.acao_navegacao === 'abrir_pedido_cancelado';
  const meta = aviso.metadados || {};
  // Pedido novo da Conecta (Douglas 14/10/2026): "é prioridade... ela tem que ter a opção
  // única de ir para a conversa". A auditoria por voz é agora: sem Vi e sem Tratar depois.
  const soAbrirConversa = !showHistorico && meta.origem === 'pedido_conecta'
    && typeof meta.cta_path === 'string' && meta.cta_path.startsWith('/');

  const abrirConversaDoPedido = async () => {
    setConfirmando(true);
    try {
      await onConfirmar(aviso.id);
    } catch { /* abre a conversa mesmo assim */ }
    setConfirmando(false);
    onClose();
    navigate(meta.cta_path);
  };

  const formatarValor = (valor: number | string | null | undefined) => {
    if (valor == null) return 'R$ 0,00';
    const num = typeof valor === 'string' ? parseFloat(valor) : valor;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatarPrazo = (prazo: string | null | undefined) => {
    if (!prazo) return null;
    try {
      return new Date(prazo).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return prazo; }
  };

  const renderCancelamentoContent = () => {
    const { item, responsaveis, cancelamento } = meta;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 bg-danger/5 dark:bg-danger/30 rounded-lg p-4 border border-danger/40 dark:border-danger">
          <span className="text-xl">❌</span>
          <div>
            <span className="text-sm font-bold uppercase tracking-wider text-brand-borgonha">Motivo</span>
            <p className="text-base font-bold text-foreground mt-1">{cancelamento?.motivo || 'Não informado'}</p>
          </div>
        </div>
        <div className="bg-muted/40 rounded-lg border border-border overflow-hidden">
          <div className="grid grid-cols-4 gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground px-4 py-2 border-b border-border bg-muted/60">
            <span>👤 Consultor</span><span>📦 Produto</span><span className="text-center">📊 Qtd</span><span className="text-right">💰 Valor</span>
          </div>
          <div className="grid grid-cols-4 gap-1 px-4 py-3 text-sm">
            <span className="font-medium text-foreground truncate">{responsaveis?.consultor || '—'}</span>
            <span className="text-foreground truncate">{item?.produto_nome || '—'}</span>
            <span className="text-center text-foreground">{item?.quantidade || '—'}</span>
            <span className="text-right font-semibold text-foreground">{formatarValor(item?.valor_total)}</span>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Cancelado por:</span>
            <span className="font-semibold text-foreground">{aviso.criado_por_nome || '—'}</span>
          </div>
          {cancelamento?.observacao && (
            <div className="flex items-start gap-2">
              <span>📝</span>
              <div>
                <span className="text-xs text-muted-foreground">Obs:</span>
                <p className="text-foreground">{cancelamento.observacao}</p>
              </div>
            </div>
          )}
          {cancelamento?.prazo_recuperacao && (
            <div className="flex items-center gap-2 bg-warning/5 dark:bg-warning/30 rounded-md px-3 py-2 border border-warning/40 dark:border-warning">
              <Clock className="w-4 h-4 text-warning" />
              <span className="text-xs text-muted-foreground">Prazo:</span>
              <span className="text-sm font-medium text-foreground">{formatarPrazo(cancelamento.prazo_recuperacao)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* vence_em vem do Postgres como "2026-10-02 16:00:00+00": vira ISO antes do
     Date, senão o Safari devolve data inválida. data_inicio não serve — o
     Tratar depois o empurra 30 minutos. */
  const isoDoBanco = (v?: string | null) => v ? v.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00') : '';
  const venceParse = ehTarefa ? new Date(isoDoBanco(aviso.metadados?.vence_em)) : null;
  const venceEm = venceParse && !Number.isNaN(venceParse.getTime()) ? venceParse : ehTarefa ? new Date(aviso.data_inicio) : null;
  const atrasoMin = venceEm ? Math.floor((Date.now() - venceEm.getTime()) / 60_000) : 0;
  const atrasoTexto = atrasoMin < 60 ? `${atrasoMin} min` : atrasoMin < 1440 ? `${Math.floor(atrasoMin / 60)} h` : `${Math.floor(atrasoMin / 1440)} d`;
  const tituloTarefa = atrasoMin > 15 ? `Tarefa vencida há ${atrasoTexto}` : 'Tarefa vencendo agora';
  const prazoTarefa = venceEm
    ? `Prazo: ${venceEm.toDateString() === new Date().toDateString() ? 'hoje' : venceEm.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${venceEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  const renderTarefaContent = () => (
    <div>
      {meta.tipo_nome && <div className="text-xs text-muted-foreground">{meta.tipo_nome}</div>}
      <div className="text-base font-semibold text-foreground">{meta.empresa || 'Cliente sem nome'}</div>
      <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2.5">
        <span className="block text-[11px] text-muted-foreground">O que você ficou de fazer</span>
        <p className="whitespace-pre-wrap text-sm text-foreground">{meta.tarefa_titulo || String(aviso.mensagem || '').split('\n')[0]}</p>
      </div>
    </div>
  );

  const renderDefaultContent = () => (
    <div>
      <p className="text-foreground whitespace-pre-wrap text-base leading-relaxed">
        {aviso.mensagem}
      </p>

      {/* Imagem do aviso */}
      {aviso.anexo_url && (
        <div className="mt-4">
          <img
            src={aviso.anexo_url}
            alt={aviso.anexo_nome || 'Imagem do aviso'}
            className="rounded-lg max-h-72 w-full object-contain border border-border"
          />
        </div>
      )}

      {aviso.metadados?.telefone_contato && (
        <div className="flex gap-3 mt-6 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="lg"
            className="flex-1 bg-success/5 hover:bg-success/10 border-success text-success"
            onClick={async () => {
              if (aviso.metadados?.lead_id && user?.id) {
                try {
                  await registrarLigacaoRealizada(aviso.metadados.lead_id, user.id, formatarTelefone(aviso.metadados.telefone_contato));
                } catch (err) { console.error('Erro ao registrar ligação:', err); }
              }
              ligar(aviso.metadados.telefone_contato);
            }}
          >
            <Phone className="h-5 w-5 mr-2" />
            Ligar Agora
          </Button>
          {aviso.metadados.url_conversa_bee && (
            <Button
              variant="outline"
              size="lg"
              className="flex-1 bg-success/5 hover:bg-success/10 border-success text-success"
              onClick={() => window.open(aviso.metadados.url_conversa_bee, '_blank')}
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              WhatsApp Bee360
            </Button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[10200] animate-in fade-in pointer-events-none" />

      <div className="fixed inset-0 z-[10200] flex items-center justify-center p-4 pointer-events-none">
        <div className={cn(
          "bg-card rounded-xl shadow-xl max-w-2xl w-full border-l-4 animate-in zoom-in-95 pointer-events-auto overflow-hidden",
          getBorderColor()
        )}>
          {/* Header */}
          <div className="flex items-start gap-3 p-5 border-b border-border">
            <div className="mt-0.5">{getIcone()}</div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-brand-borgonha leading-tight">{ehAvisoDoDia ? (cardDoDia.data?.titulo || TITULO_AVISO_DO_DIA) : ehTarefa ? tituloTarefa : aviso.titulo}</h2>
              <div className="text-xs text-muted-foreground mt-1">
                {ehTarefa ? prazoTarefa : new Date(aviso.data_inicio).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: 'long', year: 'numeric'
                })}
              </div>
            </div>
            {!aviso.requer_confirmacao && (
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Conteúdo */}
          <div className="p-5 max-h-96 overflow-y-auto">
            {isCancelamento ? renderCancelamentoContent()
              : ehAvisoDoDia ? <CorpoAvisoAtivacaoDia onAbrir={onClose} />
              : ehTarefa ? renderTarefaContent()
              : renderDefaultContent()}
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-border bg-muted/20">
            {showHistorico && (aviso.visualizado_em || aviso.confirmado_em) && (
              <div className="mb-4 text-xs text-muted-foreground space-y-1">
                {aviso.visualizado_em && (
                  <p>📖 Você visualizou em: {new Date(aviso.visualizado_em).toLocaleString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}</p>
                )}
                {aviso.confirmado_em && (
                  <p>✅ Você confirmou em: {new Date(aviso.confirmado_em).toLocaleString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}</p>
                )}
              </div>
            )}
            {soAbrirConversa ? (
              <button
                onClick={abrirConversaDoPedido}
                disabled={confirmando}
                className="w-full bg-brand-borgonha text-white px-5 py-2.5 rounded-lg hover:bg-brand-vinho transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <ArrowRight className="w-4 h-4" />
                {meta.cta_label || 'Abrir a conversa'}
              </button>
            ) : (<>
            {!showHistorico && typeof meta.cta_path === 'string' && meta.cta_path.startsWith('/') && (
              <button
                onClick={() => { onClose(); navigate(meta.cta_path); }}
                className="w-full mb-3 bg-brand-borgonha text-white px-5 py-2.5 rounded-lg hover:bg-brand-vinho transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                {meta.cta_label || 'Abrir'}
              </button>
            )}
            {!showHistorico && aviso.requer_confirmacao ? (
              <div className="flex gap-3">
                <button
                  onClick={handleTratarDepois}
                  disabled={adiando}
                  className="flex-1 bg-secondary text-secondary-foreground px-5 py-2.5 rounded-lg hover:bg-secondary/90 transition-colors font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <CalendarClock className="w-4 h-4" />
                  Tratar depois
                </button>
                <button
                  onClick={handleConfirmar}
                  disabled={confirmando}
                  className="flex-1 bg-brand-borgonha text-white px-5 py-2.5 rounded-lg hover:bg-brand-vinho transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {confirmando ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      {ehTarefa ? <MessageCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      {ehTarefa ? 'Tratar agora' : (aviso.botao_confirmacao_texto || 'Tratar agora')}
                    </>
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="w-full bg-secondary text-secondary-foreground px-5 py-2.5 rounded-lg hover:bg-secondary/90 transition-colors font-medium text-sm"
              >
                Fechar
              </button>
            )}
            </>)}
            {!showHistorico && aviso.criado_por_nome && !isCancelamento && (
              <p className="text-xs text-muted-foreground text-center mt-3">
                Criado por: {aviso.criado_por_nome}
              </p>
            )}
          </div>
        </div>
      </div>

    </>
  );
}
